import React, { useMemo } from 'react';
import { Box, Typography, Paper, useTheme, alpha, Tooltip } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import {
  validateDataSufficiency,
  minRowsForConfig,
} from '../../utils/dataSufficiency';

/**
 * PipelinePreview
 *
 * Live data-sufficiency preview. Renders one of three layouts:
 *
 *   1. SPLIT mode + segment (custom blueprints)
 *      Tri-color train/val/test bar sized by row count, with
 *      sequence-count labels under each split. Red on splits that
 *      yield 0 sequences, green otherwise. Status banner with
 *      actionable suggestions when not OK.
 *
 *   2. SINGLE mode + segment (foundation)
 *      Single horizontal bar showing how the (lookback + horizon)
 *      window fits into the segment, plus the count of distinct
 *      sliding windows the trainer can produce. Foundation trainers
 *      don't split data — they slide windows over the full series.
 *
 *   3. NO segment (blueprint wizard / backbone detail)
 *      Compact "Minimum segment size = N rows" hint, computed from
 *      the kind-aware math.
 *
 * Uses ONLY theme tokens (no hardcoded colors).
 *
 * Props:
 *   segment           - segment doc, or null if not yet picked.
 *   sequenceLength    - int. seq_len for custom, lookback for foundation.
 *   forecastHorizon   - int. From model.forecast_horizon.
 *   dataSplit         - { test_split, validation_split }. Custom-mode only.
 *   kind              - 'custom' | 'foundation'. Drives layout.
 *   compact           - bool. When true, drop the heading and tighten padding.
 */
const PipelinePreview = ({
  segment,
  sequenceLength,
  forecastHorizon,
  dataSplit,
  kind = 'custom',
  compact = false,
}) => {
  const theme = useTheme();

  const result = useMemo(() => validateDataSufficiency({
    segment, sequenceLength, forecastHorizon, dataSplit, kind,
  }), [segment, sequenceLength, forecastHorizon, dataSplit, kind]);

  // Layout 3: no segment — show minimum-rows hint instead of bars.
  if (!segment || result.status === 'unknown') {
    const minRows = minRowsForConfig({ sequenceLength, forecastHorizon, dataSplit, kind });
    if (!minRows) return null;
    return (
      <Box
        sx={{
          mt: compact ? 1 : 2,
          p: 1.5,
          borderRadius: 2,
          border: `1px dashed ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.text.primary, 0.02),
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <HelpOutlineRoundedIcon sx={{ color: theme.palette.text.secondary, fontSize: 18 }} />
        <Typography variant="body2" color="text.secondary">
          Minimum segment size for this configuration:{' '}
          <strong style={{ color: theme.palette.text.primary }}>≥ {minRows} rows</strong>
          {' '}— pick a segment with at least this many data points when you create a model.
        </Typography>
      </Box>
    );
  }

  // Have segment + computable result. Status colors from the palette.
  const { status, reason, minWindow, mode } = result;
  const statusMeta = {
    ok:    { color: theme.palette.success.main, Icon: CheckCircleRoundedIcon, label: 'Workable' },
    warn:  { color: theme.palette.warning?.main || theme.palette.primary.main, Icon: WarningAmberRoundedIcon, label: 'Workable, but small' },
    error: { color: theme.palette.error.main, Icon: ErrorRoundedIcon, label: "Won't train" },
  }[status] || { color: theme.palette.text.secondary, Icon: HelpOutlineRoundedIcon, label: 'Unknown' };

  // ---- Layout 2: foundation single-window mode ------------------------
  if (mode === 'single') {
    const { totalRows, windowCount } = result;
    const winColor = status === 'error'
      ? theme.palette.error.main
      : status === 'warn'
        ? (theme.palette.warning?.main || theme.palette.primary.main)
        : theme.palette.success.main;
    // Visualize the (lookback + horizon) window relative to total
    // segment size. The window slides — so the bar shows the SIZE of
    // one window vs the dataset, not its position.
    const windowPct = Math.min(100, (minWindow / Math.max(1, totalRows)) * 100);

    return (
      <Paper
        variant="outlined"
        sx={{
          mt: compact ? 1 : 2,
          p: compact ? 1.5 : 2,
          borderColor: alpha(statusMeta.color, 0.4),
          bgcolor: alpha(statusMeta.color, 0.03),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <statusMeta.Icon sx={{ color: statusMeta.color, fontSize: 20 }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {statusMeta.label}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
            · Foundation model — no train/val/test split
          </Typography>
        </Box>

        {/* Window-vs-dataset bar */}
        <Tooltip
          arrow
          title={
            <Box sx={{ p: 0.5 }}>
              <Typography variant="caption" sx={{ display: 'block', color: 'inherit' }}>
                One sliding window = lookback + horizon = {minWindow} rows
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: 'inherit' }}>
                Segment total = {totalRows.toLocaleString()} rows
              </Typography>
            </Box>
          }
        >
          <Box
            sx={{
              height: 28,
              width: '100%',
              borderRadius: 1,
              overflow: 'hidden',
              border: `1px solid ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.text.primary, 0.05),
              position: 'relative',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 0, bottom: 0, left: 0,
                width: `${windowPct}%`,
                bgcolor: winColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: theme.palette.getContrastText(winColor),
                fontSize: '11px',
                fontWeight: 600,
                minWidth: 0,
              }}
            >
              {windowPct >= 12 && `Window ${minWindow}`}
            </Box>
          </Box>
        </Tooltip>

        {/* Window-count + total-rows summary */}
        <Box
          sx={{
            mt: 1,
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 1,
          }}
        >
          <SingleStat label="Total rows"      value={totalRows.toLocaleString()}      theme={theme} />
          <SingleStat label="Window size"     value={`${minWindow} rows`}             theme={theme} />
          <SingleStat label="Sliding windows" value={windowCount.toLocaleString()}    theme={theme} color={winColor} />
        </Box>

        {status !== 'ok' && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 1.5,
              color: status === 'error' ? theme.palette.error.main : theme.palette.text.secondary,
              lineHeight: 1.5,
            }}
          >
            {reason}
          </Typography>
        )}
      </Paper>
    );
  }

  // ---- Layout 1: custom split-bar mode --------------------------------
  const { splits, sequences } = result;
  const total = Math.max(1, splits.totalRows);
  const trainPct = (splits.trainRows / total) * 100;
  const valPct   = (splits.valRows   / total) * 100;
  const testPct  = (splits.testRows  / total) * 100;

  // Per-split colors: green if ok, red if zero sequences, neutral otherwise.
  const splitColor = (count) => {
    if (count === 0) return theme.palette.error.main;
    if (count < 5)   return theme.palette.warning?.main || theme.palette.primary.main;
    return theme.palette.success.main;
  };

  const SplitTooltip = ({ name, rows, sequenceCount }) => (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="body2" sx={{ fontWeight: 700, color: 'inherit', mb: 0.5 }}>
        {name}
      </Typography>
      <Typography variant="caption" sx={{ color: 'inherit', display: 'block' }}>
        {rows} rows
      </Typography>
      <Typography variant="caption" sx={{ color: 'inherit', display: 'block' }}>
        {sequenceCount} training sequence{sequenceCount === 1 ? '' : 's'}
      </Typography>
    </Box>
  );

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: compact ? 1 : 2,
        p: compact ? 1.5 : 2,
        borderColor: alpha(statusMeta.color, 0.4),
        bgcolor: alpha(statusMeta.color, 0.03),
      }}
    >
      {/* Header: status icon + label + summary */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <statusMeta.Icon sx={{ color: statusMeta.color, fontSize: 20 }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {statusMeta.label}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
          · {splits.totalRows.toLocaleString()} total rows · window = {minWindow}
        </Typography>
      </Box>

      {/* Split bar */}
      <Box
        sx={{
          height: 28,
          width: '100%',
          borderRadius: 1,
          overflow: 'hidden',
          display: 'flex',
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Tooltip title={<SplitTooltip name="Train" rows={splits.trainRows} sequenceCount={sequences.train} />} arrow>
          <Box
            sx={{
              flex: trainPct,
              bgcolor: splitColor(sequences.train),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette.getContrastText(splitColor(sequences.train)),
              fontSize: '11px',
              fontWeight: 600,
              minWidth: 0,
            }}
          >
            {trainPct >= 8 && `Train ${Math.round(trainPct)}%`}
          </Box>
        </Tooltip>
        <Tooltip title={<SplitTooltip name="Validation" rows={splits.valRows} sequenceCount={sequences.val} />} arrow>
          <Box
            sx={{
              flex: valPct,
              bgcolor: splitColor(sequences.val),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette.getContrastText(splitColor(sequences.val)),
              fontSize: '11px',
              fontWeight: 600,
              minWidth: 0,
            }}
          >
            {valPct >= 8 && `Val ${Math.round(valPct)}%`}
          </Box>
        </Tooltip>
        <Tooltip title={<SplitTooltip name="Test" rows={splits.testRows} sequenceCount={sequences.test} />} arrow>
          <Box
            sx={{
              flex: testPct,
              bgcolor: splitColor(sequences.test),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: theme.palette.getContrastText(splitColor(sequences.test)),
              fontSize: '11px',
              fontWeight: 600,
              minWidth: 0,
            }}
          >
            {testPct >= 8 && `Test ${Math.round(testPct)}%`}
          </Box>
        </Tooltip>
      </Box>

      {/* Per-split sequence-count labels */}
      <Box
        sx={{
          mt: 1,
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 1,
        }}
      >
        <SplitLabel label="Train"      rows={splits.trainRows} count={sequences.train} theme={theme} splitColor={splitColor} />
        <SplitLabel label="Validation" rows={splits.valRows}   count={sequences.val}   theme={theme} splitColor={splitColor} />
        <SplitLabel label="Test"       rows={splits.testRows}  count={sequences.test}  theme={theme} splitColor={splitColor} />
      </Box>

      {/* Status message (only when not OK — keeps the OK case clean). */}
      {status !== 'ok' && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 1.5,
            color: status === 'error' ? theme.palette.error.main : theme.palette.text.secondary,
            lineHeight: 1.5,
          }}
        >
          {reason}
        </Typography>
      )}
    </Paper>
  );
};

const SplitLabel = ({ label, rows, count, theme, splitColor }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: theme.palette.text.primary }}>
      {label}
    </Typography>
    <Typography variant="caption" sx={{ display: 'block', color: splitColor(count), fontWeight: 600 }}>
      {count.toLocaleString()} sequence{count === 1 ? '' : 's'}
    </Typography>
    <Typography variant="caption" sx={{ display: 'block', color: theme.palette.text.secondary, fontSize: '10px' }}>
      ({rows.toLocaleString()} rows)
    </Typography>
  </Box>
);

const SingleStat = ({ label, value, theme, color }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Typography variant="caption" sx={{ display: 'block', color: theme.palette.text.secondary, fontSize: '10px' }}>
      {label}
    </Typography>
    <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: color || theme.palette.text.primary, fontSize: '13px' }}>
      {value}
    </Typography>
  </Box>
);

export default PipelinePreview;
